import { flow, makeAutoObservable, observable } from "mobx";
import { v4 } from "uuid";

import {
  ComputerAgentDataStatus,
  MessageRole,
  StreamEventType,
  ToolCallStatus,
} from "@/components/VibeChat/types";
import { dispatchEvent } from "@/components/VibeChat/utils/eventHandlers";
import { mergeAnalyticsGraphChartDataIntoToolOutput } from "@/components/VibeChat/utils/analyticsGraphToolData";
import type { VibeChatToolResultPayload } from "@/components/VibeChat/utils/invalidateSmartTableQueries";
import { loadChatHistory } from "@/components/VibeChat/utils/chatHistoryLoader";
import { convertMessagesToConversationHistory } from "@/components/VibeChat/utils/chatHistorySerializer";
//Stores
import { SessionStore } from "@/components/VibeChat/stores/session-store";
import { MessageStore } from "@/components/VibeChat/stores/message-store";
import { StreamStore } from "@/components/VibeChat/stores/stream-store";
//Utils
import { displayErrorToast } from "@/utils/toast";
import type { ConversationMessage } from "@/queries/vibe/useRecentVibeChatSessions";
//Types
import {
  ChatMessage,
  ChatSession,
  ToolCallData,
  ComputerAgentData,
  NavigationItem,
  QueuedChatMessage,
} from "@/components/VibeChat/stores/types";
import type { AnalyticsGraphToolChartData } from "@/queries/analytics-custom-charts-types";

const parseToolInputRecord = (toolInput: unknown): Record<string, unknown> | undefined => {
  if (typeof toolInput === "string") {
    try {
      const parsed = JSON.parse(toolInput);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return undefined;
    }
  }
  if (toolInput && typeof toolInput === "object" && !Array.isArray(toolInput)) {
    return toolInput as Record<string, unknown>;
  }
  return undefined;
};

const parseToolResultRecord = (
  output: unknown,
  result: unknown,
): Record<string, unknown> | undefined => {
  const direct =
    result && typeof result === "object" && !Array.isArray(result)
      ? (result as Record<string, unknown>)
      : null;
  if (direct) {
    return direct;
  }
  if (typeof output === "string") {
    try {
      const parsed = JSON.parse(output);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return undefined;
    }
  }
  if (output && typeof output === "object" && !Array.isArray(output)) {
    return output as Record<string, unknown>;
  }
  return undefined;
};

// Re-export types for backward compatibility
export type {
  ChatMessage,
  ChatSession,
  ToolCallData,
  ComputerAgentData,
  NavigationItem,
  QueuedChatMessage,
};

export type StreamEventListener = (
  eventType: StreamEventType,
  data: Record<string, unknown>,
) => void;

export class VibeChatStore {
  readonly sessions: SessionStore;
  private readonly _messageStore: MessageStore;
  readonly stream: StreamStore;
  private _eventListeners = new Set<StreamEventListener>();
  private readonly messageQueueByChatId = observable.map<string, QueuedChatMessage[]>();
  /** Tracks session id renames so queued messages still drain after SESSION_CREATED. */
  private readonly chatIdRedirects = observable.map<string, string>();
  private readonly editingQueuedMessageIdByChatId = observable.map<string, string>();
  isFollowMode = false;
  isFollowModeDisabled = false;

  constructor() {
    this.sessions = new SessionStore();
    this._messageStore = new MessageStore(this.sessions);
    this.stream = new StreamStore();
    makeAutoObservable<
      VibeChatStore,
      | "_eventListeners"
      | "messageQueueByChatId"
      | "chatIdRedirects"
      | "editingQueuedMessageIdByChatId"
      | "isChatStreaming"
      | "resolveChatId"
      | "isQueueDrainPaused"
      | "getNextDrainMessage"
      | "migrateChatIdentity"
      | "abortStreamForChat"
    >(this, {
      _eventListeners: false,
      messageQueueByChatId: false,
      chatIdRedirects: false,
      editingQueuedMessageIdByChatId: false,
      isLoadingForChat: false,
      isChatStreaming: false,
      resolveChatId: false,
      isQueueDrainPaused: false,
      getNextDrainMessage: false,
      migrateChatIdentity: false,
      abortStreamForChat: false,
      getConversationHistoryForChat: false,
      getQueuedMessageCount: false,
      getQueuedMessages: false,
    });
  }

  toggleFollowMode() {
    if (this.isFollowModeDisabled) {
      return;
    }

    this.isFollowMode = !this.isFollowMode;
  }

  setFollowMode(value: boolean) {
    if (value && this.isFollowModeDisabled) {
      return;
    }

    this.isFollowMode = value;
  }

  setFollowModeDisabled(value: boolean) {
    this.isFollowModeDisabled = value;
    if (value) {
      this.isFollowMode = false;
    }
  }

  addEventListener(listener: StreamEventListener): () => void {
    this._eventListeners.add(listener);
    return () => {
      this._eventListeners.delete(listener);
    };
  }

  // Computed getters delegate to sub-stores
  get messages(): ChatMessage[] {
    return this._messageStore.currentMessages;
  }

  get messagesByChatId(): Map<string, ChatMessage[]> {
    return this._messageStore.messagesByChatIdMap;
  }

  get chatSessions(): ChatSession[] {
    return this.sessions.chatSessions;
  }

  get currentChatId(): string {
    return this.sessions.currentChatId;
  }

  get currentChat(): ChatSession | undefined {
    return this.sessions.currentSession;
  }

  get isLoading(): boolean {
    const chatId = this.currentChatId;
    return chatId ? this.isChatStreaming(chatId) : false;
  }

  get isAnyLoading(): boolean {
    return this.stream.isAnyLoading;
  }

  get hasStreamingStarted(): boolean {
    const chatId = this.currentChatId;
    return chatId ? this.stream.hasStreamingStartedFor(chatId) : false;
  }

  get isReceivingEvents(): boolean {
    const chatId = this.currentChatId;
    return chatId ? this.stream.isReceivingEventsFor(chatId) : false;
  }

  get showThinkingAfterToolCall(): boolean {
    const chatId = this.currentChatId;
    return chatId ? this.stream.showThinkingAfterToolCallFor(chatId) : false;
  }

  isLoadingForChat(chatId: string): boolean {
    return this.isChatStreaming(chatId);
  }

  private isChatStreaming(chatId: string): boolean {
    if (!chatId) {
      return false;
    }

    const resolvedChatId = this.resolveChatId(chatId);
    if (this.stream.isLoadingFor(chatId) || this.stream.isLoadingFor(resolvedChatId)) {
      return true;
    }

    // Stream state stays under the pre-SESSION_CREATED id while redirects track the rename.
    for (const activeChatId of this.stream.getActiveStreamChatIds()) {
      if (this.resolveChatId(activeChatId) === resolvedChatId) {
        return true;
      }
    }

    return false;
  }

  get queuedMessages(): QueuedChatMessage[] {
    return this.getQueuedMessages(this.currentChatId);
  }

  get editingQueuedMessageId(): string | null {
    return this.getEditingQueuedMessageId(this.currentChatId);
  }

  getEditingQueuedMessageId(chatId: string): string | null {
    if (!chatId) {
      return null;
    }
    return this.editingQueuedMessageIdByChatId.get(this.resolveChatId(chatId)) ?? null;
  }

  getQueuedMessageCount(chatId: string): number {
    return this.getQueuedMessages(chatId).length;
  }

  getQueuedMessages(chatId: string): QueuedChatMessage[] {
    if (!chatId) {
      return [];
    }
    return [...(this.messageQueueByChatId.get(this.resolveChatId(chatId)) ?? [])];
  }

  enqueueMessage(message: string, chatId = this.currentChatId) {
    const trimmed = message.trim();
    if (!chatId || !trimmed) {
      return;
    }

    const resolvedChatId = this.resolveChatId(chatId);
    const queue = this.messageQueueByChatId.get(resolvedChatId) ?? [];
    this.messageQueueByChatId.set(resolvedChatId, [
      ...queue,
      {
        id: v4(),
        content: trimmed,
      },
    ]);
  }

  reorderQueuedMessages(orderedIds: string[], chatId = this.currentChatId) {
    if (!chatId || orderedIds.length === 0) {
      return;
    }

    const resolvedChatId = this.resolveChatId(chatId);
    const queue = this.messageQueueByChatId.get(resolvedChatId) ?? [];
    if (queue.length === 0) {
      return;
    }

    const byId = new Map(queue.map(item => [item.id, item]));
    const next: QueuedChatMessage[] = [];
    for (const id of orderedIds) {
      const item = byId.get(id);
      if (item) {
        next.push(item);
        byId.delete(id);
      }
    }
    for (const item of byId.values()) {
      next.push(item);
    }
    this.messageQueueByChatId.set(resolvedChatId, next);
  }

  removeQueuedMessage(messageId: string, chatId = this.currentChatId) {
    if (!chatId || !messageId) {
      return;
    }

    const resolvedChatId = this.resolveChatId(chatId);
    const queue = this.messageQueueByChatId.get(resolvedChatId) ?? [];
    const next = queue.filter(item => item.id !== messageId);
    if (this.getEditingQueuedMessageId(resolvedChatId) === messageId) {
      this.editingQueuedMessageIdByChatId.delete(resolvedChatId);
    }
    if (next.length === 0) {
      this.messageQueueByChatId.delete(resolvedChatId);
      return;
    }
    this.messageQueueByChatId.set(resolvedChatId, next);
  }

  updateQueuedMessage(messageId: string, content: string, chatId = this.currentChatId) {
    const trimmed = content.trim();
    if (!chatId || !messageId || !trimmed) {
      return;
    }

    const resolvedChatId = this.resolveChatId(chatId);
    const queue = this.messageQueueByChatId.get(resolvedChatId) ?? [];
    const next = queue.map(item => (item.id === messageId ? { ...item, content: trimmed } : item));
    this.messageQueueByChatId.set(resolvedChatId, next);
  }

  clearMessageQueue(chatId = this.currentChatId) {
    if (!chatId) {
      return;
    }
    const resolvedChatId = this.resolveChatId(chatId);
    this.messageQueueByChatId.delete(chatId);
    this.messageQueueByChatId.delete(resolvedChatId);
    this.editingQueuedMessageIdByChatId.delete(chatId);
    this.editingQueuedMessageIdByChatId.delete(resolvedChatId);
  }

  setEditingQueuedMessageId(messageId: string | null, chatId = this.currentChatId) {
    if (!chatId) {
      return;
    }
    const resolvedChatId = this.resolveChatId(chatId);
    if (!messageId) {
      this.editingQueuedMessageIdByChatId.delete(resolvedChatId);
      return;
    }
    this.editingQueuedMessageIdByChatId.set(resolvedChatId, messageId);
  }

  private isQueueDrainPaused(chatId: string): boolean {
    const editingId = this.getEditingQueuedMessageId(chatId);
    if (!editingId) {
      return false;
    }
    const [first] = this.getQueuedMessages(chatId);
    return first?.id === editingId;
  }

  tryDrainMessageQueue = flow(function* tryDrainMessageQueueGenerator(
    this: VibeChatStore,
    workspaceId: number,
    callbacks?: {
      onStreamComplete?: () => void;
      onAgentProgress?: () => void;
      onAgentComplete?: () => void;
      onToolResult?: (payload: VibeChatToolResultPayload) => void;
      chatId?: string;
    },
  ) {
    if (!workspaceId) {
      return;
    }

    let chatId = callbacks?.chatId ?? this.currentChatId;
    if (!chatId) {
      return;
    }

    chatId = this.resolveChatId(chatId);
    const nextMessage = this.getNextDrainMessage(chatId);
    if (nextMessage) {
      yield this.handleSendMessage(nextMessage, workspaceId, {
        ...callbacks,
        chatId,
      });
    }
  });

  private getNextDrainMessage(chatId: string): string | undefined {
    const resolvedChatId = this.resolveChatId(chatId);
    if (this.isChatStreaming(resolvedChatId) || this.isQueueDrainPaused(resolvedChatId)) {
      return undefined;
    }
    return this.dequeueMessage(resolvedChatId);
  }

  private dequeueMessage(chatId: string): string | undefined {
    const resolvedChatId = this.resolveChatId(chatId);
    const queue = this.messageQueueByChatId.get(resolvedChatId);
    if (!queue?.length) {
      return undefined;
    }

    const [next, ...rest] = queue;
    if (rest.length === 0) {
      this.messageQueueByChatId.delete(resolvedChatId);
    } else {
      this.messageQueueByChatId.set(resolvedChatId, rest);
    }
    return next.content;
  }

  private migrateChatIdentity(fromChatId: string, toChatId: string) {
    if (!fromChatId || !toChatId || fromChatId === toChatId) {
      return;
    }

    // Keep stream state under the original request id; redirects make lookups resolve.
    this.chatIdRedirects.set(fromChatId, toChatId);

    const editingId = this.editingQueuedMessageIdByChatId.get(fromChatId);
    if (editingId) {
      this.editingQueuedMessageIdByChatId.set(toChatId, editingId);
      this.editingQueuedMessageIdByChatId.delete(fromChatId);
    }

    const queue = this.messageQueueByChatId.get(fromChatId);
    if (!queue?.length) {
      this.messageQueueByChatId.delete(fromChatId);
      return;
    }

    const existing = this.messageQueueByChatId.get(toChatId) ?? [];
    this.messageQueueByChatId.set(toChatId, [...existing, ...queue]);
    this.messageQueueByChatId.delete(fromChatId);
  }

  private resolveChatId(chatId: string): string {
    let resolved = chatId;
    const seen = new Set<string>();
    while (this.chatIdRedirects.has(resolved) && !seen.has(resolved)) {
      seen.add(resolved);
      resolved = this.chatIdRedirects.get(resolved)!;
    }
    return resolved;
  }

  private clearAllMessageQueues() {
    this.messageQueueByChatId.clear();
    this.chatIdRedirects.clear();
    this.editingQueuedMessageIdByChatId.clear();
  }

  getConversationHistoryForChat(chatId: string): ConversationMessage[] {
    return convertMessagesToConversationHistory(this._messageStore.getMessages(chatId));
  }

  get currentConversationHistory(): ConversationMessage[] {
    return this.getConversationHistoryForChat(this.currentChatId);
  }

  recordAnalyticsGraphChartData(
    toolCallId: string | undefined,
    chartData: AnalyticsGraphToolChartData,
  ) {
    const chatId = this.currentChatId;
    if (!chatId || !toolCallId) {
      return;
    }

    const messageIndex = this._messageStore.findToolCallToUpdate(chatId, toolCallId);
    if (messageIndex === -1) {
      return;
    }

    this._messageStore.updateAt(chatId, messageIndex, message => {
      if (!message.toolCall) {
        return message;
      }

      return {
        ...message,
        toolCall: {
          ...message.toolCall,
          output: mergeAnalyticsGraphChartDataIntoToolOutput(message.toolCall.output, chartData),
        },
      };
    });
  }

  setAuthToken(token: string | null) {
    this.stream.setAuthToken(token);
  }

  createNewChat(): string {
    const chatId = this.sessions.create();
    this._messageStore.initializeChat(chatId);
    this._messageStore.resetState(chatId);
    return chatId;
  }

  selectChat(chatId: string) {
    this.sessions.select(chatId);
    this._messageStore.initializeChat(chatId);
  }

  addSessionsFromRequest(sessions: ChatSession[]) {
    this.sessions.addSessionsFromRequest(sessions);
  }

  loadChatHistory(
    chatId: string,
    conversationHistory: {
      role: "user" | "assistant" | "tool";
      content: { type: "text"; text: string }[] | string;
      tool_calls?: {
        id: string;
        type: string;
        function: {
          name: string;
          arguments: string;
        };
      }[];
      tool_call_id?: string;
    }[],
  ) {
    const existingMessages = this._messageStore.getMessages(chatId);
    if (
      this.currentChatId === chatId &&
      ((existingMessages && existingMessages.length > 0) || this.stream.isLoadingFor(chatId))
    ) {
      return;
    }

    const messages = loadChatHistory(conversationHistory);
    this._messageStore.setMessages(chatId, messages);
  }

  updateChatTitle(chatId: string, title: string) {
    this.sessions.updateTitle(chatId, title);
  }

  updateSessionName(sessionId: string, name: string) {
    this.sessions.updateName(sessionId, name);
  }

  setSessionNameGenerating(sessionId: string, isGenerating: boolean) {
    this.sessions.setNameGenerating(sessionId, isGenerating);
  }

  updateSessionNamePreview(sessionId: string, accumulatedName: string) {
    this.sessions.updateNamePreview(sessionId, accumulatedName);
  }

  setChatSessions(sessions: ChatSession[]) {
    this.sessions.setSessions(sessions);
  }

  removeSession(sessionId: string) {
    this.stream.abort(sessionId);
    this.clearMessageQueue(sessionId);
    this.sessions.remove(sessionId);
    this._messageStore.deleteChat(sessionId);
  }

  clearAllSessions() {
    for (const chatId of this.stream.getActiveStreamChatIds()) {
      this.stream.abort(chatId);
    }
    this.clearAllMessageQueues();
    this.sessions.clearAll();
    this._messageStore.clearAll();
  }

  clearMessages() {
    const chatId = this.currentChatId;
    if (!chatId) {
      return;
    }
    this._messageStore.clear(chatId);
    this.stream.cleanup(chatId);
  }

  abortRequest(chatId?: string) {
    const targetChatId = chatId ?? this.currentChatId;
    if (!targetChatId) {
      return;
    }

    const chatMessages = this._messageStore.getMessages(targetChatId);

    let cancelledLastAgent = false;
    for (let i = chatMessages.length - 1; i >= 0; i--) {
      const msg = chatMessages[i];
      if (
        msg?.role === MessageRole.TOOL_CALL &&
        msg.toolCall &&
        msg.toolCall.status === ToolCallStatus.RUNNING
      ) {
        this._messageStore.set(targetChatId, i, {
          ...msg,
          toolCall: {
            ...msg.toolCall,
            status: ToolCallStatus.FAILED,
            completedAt: Date.now(),
          },
        });
      }
      if (
        msg?.role === MessageRole.COMPUTER_AGENT &&
        msg.computerAgent &&
        msg.computerAgent.status === ComputerAgentDataStatus.RUNNING
      ) {
        if (!cancelledLastAgent) {
          this._messageStore.set(targetChatId, i, {
            ...msg,
            computerAgent: {
              ...msg.computerAgent,
              status: ComputerAgentDataStatus.CANCELLED,
            },
          });
          cancelledLastAgent = true;
        } else {
          this._messageStore.set(targetChatId, i, {
            ...msg,
            computerAgent: {
              ...msg.computerAgent,
              status: ComputerAgentDataStatus.COMPLETED,
            },
          });
        }
      }
    }

    // Stop preserves queued follow-ups so users can redirect the conversation.
    this.abortStreamForChat(targetChatId);
  }

  private abortStreamForChat(chatId: string) {
    const resolvedChatId = this.resolveChatId(chatId);
    const chatIds = new Set<string>([chatId, resolvedChatId]);
    for (const activeChatId of this.stream.getActiveStreamChatIds()) {
      if (this.resolveChatId(activeChatId) === resolvedChatId) {
        chatIds.add(activeChatId);
      }
    }
    for (const streamChatId of chatIds) {
      this.stream.abort(streamChatId);
    }
  }

  takeQueuedMessage(messageId: string, chatId = this.currentChatId): string | undefined {
    if (!chatId || !messageId) {
      return undefined;
    }

    const resolvedChatId = this.resolveChatId(chatId);
    const queue = this.messageQueueByChatId.get(resolvedChatId) ?? [];
    const item = queue.find(queued => queued.id === messageId);
    if (!item) {
      return undefined;
    }

    this.removeQueuedMessage(messageId, resolvedChatId);
    return item.content;
  }

  handleSendMessage = flow(function* handleSendMessageGenerator(
    this: VibeChatStore,
    message: string,
    workspaceId: number,
    callbacks?: {
      onStreamComplete?: () => void;
      onAgentProgress?: () => void;
      onAgentComplete?: () => void;
      onToolResult?: (payload: VibeChatToolResultPayload) => void;
      chatId?: string;
    },
  ): Generator<Promise<unknown>, void, unknown> {
    if (!workspaceId) {
      return;
    }

    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      return;
    }

    let chatId = callbacks?.chatId ?? this.currentChatId;
    if (!chatId) {
      chatId = this.createNewChat();
    }

    if (this.isChatStreaming(chatId)) {
      this.enqueueMessage(trimmedMessage, chatId);
      return;
    }

    this._messageStore.resetState(chatId);
    this._messageStore.removeTrailingEmptyAssistants(chatId);

    const conversationHistory = convertMessagesToConversationHistory(
      this._messageStore.getMessages(chatId),
    );

    const userMessageId = `user-${Date.now()}-${Math.random()}`;
    this._messageStore.setLastUserMessageId(chatId, userMessageId);
    this._messageStore.push(chatId, { role: MessageRole.USER, content: trimmedMessage });

    this._messageStore.setCurrentMessageRole(chatId, MessageRole.ASSISTANT);
    const chatMessages = this._messageStore.getMessages(chatId);
    this._messageStore.setAssistantMessageIndex(chatId, chatMessages.length);
    this._messageStore.setIsFirstDelta(chatId, true);
    this._messageStore.push(chatId, { role: MessageRole.ASSISTANT, content: "" });

    let wasAborted = false;
    try {
      yield this.stream.sendMessage(
        chatId,
        {
          message: trimmedMessage,
          conversation_history: conversationHistory,
          workspace_id: workspaceId,
          current_url: window.location.href,
          session_id: chatId,
        },
        {
          onEvent: (line: string) => {
            this.parseAndHandleEvent(line, chatId, callbacks);
          },
          onNonStreamingResponse: result => {
            this.handleNonStreamingResponse(chatId, result);
          },
          onStreamComplete: callbacks?.onStreamComplete,
        },
      );
    } catch (error: unknown) {
      const err = error as { name?: string; message?: string };
      if (err.name === "AbortError") {
        wasAborted = true;
      } else {
        this.handleErrorState(chatId, err.message || "Failed to get response");
      }
    }

    if (wasAborted) {
      return;
    }

    const drainChatId = this.resolveChatId(chatId);
    const nextMessage = this.getNextDrainMessage(drainChatId);
    if (nextMessage) {
      yield this.handleSendMessage(nextMessage, workspaceId, {
        ...callbacks,
        chatId: drainChatId,
      });
    }
  });

  popUserMessageForRetry(messageIndex?: number): string | null {
    const chatId = this.currentChatId;
    if (!chatId) {
      return null;
    }

    const messages = this._messageStore.getMessages(chatId);
    const requestedMessage =
      messageIndex !== undefined && messageIndex >= 0 && messageIndex < messages.length
        ? messages[messageIndex]
        : undefined;
    let userMessageIndex =
      requestedMessage?.role === MessageRole.USER && requestedMessage.content
        ? messageIndex
        : undefined;

    if (userMessageIndex === undefined) {
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === MessageRole.USER) {
          userMessageIndex = i;
          break;
        }
      }
    }

    if (userMessageIndex === undefined) {
      return null;
    }

    const userMessage = messages[userMessageIndex];
    this._messageStore.setMessages(chatId, messages.slice(0, userMessageIndex));

    return userMessage.content;
  }

  private parseAndHandleEvent(
    line: string,
    chatId: string,
    callbacks?: {
      onAgentProgress?: () => void;
      onAgentComplete?: () => void;
      onToolResult?: (payload: VibeChatToolResultPayload) => void;
    },
  ) {
    try {
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) {
        return;
      }

      const data = JSON.parse(jsonStr) as Record<string, unknown>;
      const eventType = data.type as StreamEventType;
      dispatchEvent(eventType, data, {
        chatId,
        sessions: this.sessions,
        messages: this._messageStore,
        stream: this.stream,
        onStreamError: (errorMessage: string) => {
          this.handleErrorState(chatId, errorMessage);
        },
      });

      if (
        (eventType === StreamEventType.SESSION_CREATED ||
          eventType === StreamEventType.SESSION_NAME_GENERATION_STARTED) &&
        typeof data.session_id === "string" &&
        data.session_id !== chatId
      ) {
        this.migrateChatIdentity(chatId, data.session_id);
      }

      if (callbacks?.onAgentProgress && eventType === StreamEventType.COMPUTER_AGENT_PROGRESS) {
        callbacks.onAgentProgress();
      }
      if (callbacks?.onAgentComplete && eventType === StreamEventType.COMPUTER_AGENT_COMPLETE) {
        callbacks.onAgentComplete();
      }
      if (callbacks?.onToolResult && eventType === StreamEventType.TOOL_RESULT) {
        const toolCallId = data.tool_call_id as string | undefined;
        const toolNameFromEvent = data.tool_name as string | undefined;
        const chatMessages = this._messageStore.getMessages(chatId);
        const matchingToolCall = toolCallId
          ? chatMessages.find(
              message =>
                message.role === MessageRole.TOOL_CALL && message.toolCall?.id === toolCallId,
            )?.toolCall
          : undefined;

        callbacks.onToolResult({
          toolName: toolNameFromEvent ?? matchingToolCall?.name,
          toolInput: matchingToolCall?.input ?? parseToolInputRecord(data.tool_input),
          result: parseToolResultRecord(data.output, data.result),
        });
      }

      for (const listener of this._eventListeners) {
        listener(eventType, data);
      }
    } catch {
      // nothing
    }
  }

  private handleErrorState(chatId: string, errorMessage: string) {
    const messages = this._messageStore.getMessages(chatId);
    let lastUserMessageIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === MessageRole.USER) {
        lastUserMessageIndex = i;
        break;
      }
    }
    if (lastUserMessageIndex !== -1) {
      this._messageStore.setMessages(chatId, messages.slice(0, lastUserMessageIndex + 1));
    }
    this.stream.abort(chatId);
    displayErrorToast(errorMessage);
  }

  private handleNonStreamingResponse(
    chatId: string,
    result: {
      response?: string;
      message?: string;
      debug?: Record<string, unknown>;
    },
  ) {
    const messages = this._messageStore.getMessages(chatId);
    const assistantIndex = this._messageStore.getAssistantMessageIndex(chatId);
    if (result.response) {
      if (assistantIndex >= 0 && assistantIndex < messages.length) {
        const currentMsg = messages[assistantIndex];
        this._messageStore.set(chatId, assistantIndex, {
          ...currentMsg,
          content: result.response,
          debug: result.debug,
        });
      }
    }
  }
}
